import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  keyframes,
  Chip,
  Button,
  Grid,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Receipt as ReceiptIcon,
  ViewList as ViewListIcon,
  ShoppingBasket as OrdersIcon,
  AssignmentReturn as ReturnsIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  Straighten as StraightenIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  CreditCard as CreditCardIcon,
  Assessment as AssessmentIcon,
  PointOfSale as PointOfSaleIcon,
  Apps as AppsIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  LocationCity as LocationCityIcon,
  SupervisorAccount as UsersIcon,
  TrendingUp as TrendingUpIcon,
  Print as PrintIcon,
  Web as WebIcon,
  QrCode2 as QrCodeIcon,
  CardGiftcard as CardGiftcardIcon,
  Inventory2 as RepackIcon,
  Layers as LayersIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { isAdmin, logout, getCurrentUser } from '../../services/authService';
import companySettingsService from '../../services/companySettingsService';
import OfflineSyncIndicator from '../offline/OfflineSyncIndicator';
import { getCurrentSubscription } from '../../services/subscriptionService';
import notificationService from '../../services/notificationService';
import useNotificationSound from '../../hooks/useNotificationSound';
import { useNotifications } from '../../context/NotificationContext';

// Drawer width when opened
const DRAWER_WIDTH = 240;

// Define the pulse animation keyframes
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Define the notification dot animation
const notificationDotAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Main navigation component with responsive drawer
 * @returns {JSX.Element} Navigation component
 */
const Navigation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(getCurrentUser());
  /** When false, batch-wise UI (POS, purchase, product batch tab, batch report) is hidden — mirrors Company Settings. */
  const [enableBatchWise, setEnableBatchWise] = useState(true);
  const { playSound } = useNotificationSound();

  // Company user refresh + batch-wise flag (reload when company info or settings change)
  useEffect(() => {
    let cancelled = false;
    const loadBatchWiseSetting = () => {
      companySettingsService
        .getSettings()
        .then((data) => {
          if (!cancelled) setEnableBatchWise(Boolean(data?.enableBatchWise ?? true));
        })
        .catch(() => {
          if (!cancelled) setEnableBatchWise(true);
        });
    };

    const handleCompanyUpdate = () => {
      setUser(getCurrentUser());
      loadBatchWiseSetting();
    };

    loadBatchWiseSetting();

    window.addEventListener('companyInfoUpdated', handleCompanyUpdate);
    window.addEventListener('storage', handleCompanyUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener('companyInfoUpdated', handleCompanyUpdate);
      window.removeEventListener('storage', handleCompanyUpdate);
    };
  }, []);

  // Use centralized notification context instead of local state
  const { notifications, unreadCount, fetchUnreadNotifications } = useNotifications();

  // Check if screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for drawer, notification menu, user menu, and menu expansions
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [navAnchorEls, setNavAnchorEls] = useState({});



  const handleNavMenuOpen = (event, menu) => {
    setNavAnchorEls({ [menu]: event.currentTarget });
  };

  const handleNavMenuClose = (menu) => {
    setNavAnchorEls(prev => ({ ...prev, [menu]: null }));
  };



  const megaNavItems = {
    Master: [
      {
        title: 'Inventory',
        items: [
          { label: 'Products', path: '/inventory/products', icon: <ViewListIcon fontSize="small" /> },
          { label: 'Excel Product Entry', path: '/inventory/quick-product-entry', icon: <ViewListIcon fontSize="small" /> },
          { label: 'Categories', path: '/inventory/categories', icon: <CategoryIcon fontSize="small" /> },
          { label: 'Units', path: '/inventory/units', icon: <StraightenIcon fontSize="small" /> },
          { label: 'Tax Rates', path: '/inventory/taxes', icon: <CurrencyRupeeIcon fontSize="small" /> },
          { label: 'Import Products', path: '/inventory/import-products', icon: <InventoryIcon fontSize="small" /> },
          { label: 'Barcode / QR Generate', path: '/inventory/barcode-generate', icon: <QrCodeIcon fontSize="small" /> },
          { label: 'Stock Transfer', path: '/inventory/stock-transfer', icon: <InventoryIcon fontSize="small" /> },
          { label: 'Physical Stock Update', path: '/inventory/physical-stock-update', icon: <AssessmentIcon fontSize="small" /> },
          { label: 'Repacking Management', path: '/inventory/repacking', icon: <RepackIcon fontSize="small" /> },
        ]
      },
      {
        title: 'People & Locations',
        items: [
          { label: 'Customers', path: '/sales/customers', icon: <PersonIcon fontSize="small" /> },
          { label: 'Suppliers', path: '/purchases/suppliers', icon: <BusinessIcon fontSize="small" /> },
          { label: 'Users', path: '/people/users', icon: <PersonIcon fontSize="small" /> },
          { label: 'Salesmen', path: '/people/salesmen', icon: <PersonIcon fontSize="small" /> },
          { label: 'Locations', path: '/inventory/locations', icon: <LocationCityIcon fontSize="small" /> },
          { label: 'Location Users', path: '/inventory/location-users', icon: <UsersIcon fontSize="small" /> },
        ]
      },
      {
        title: 'Physical Stock Management',
        items: [
          { label: 'Stock Entry', path: '/inventory/physical-stock-entry', icon: <InventoryIcon fontSize="small" /> },
          { label: 'View Stock Entry', path: '/inventory/view-physical-stock-entry', icon: <VisibilityIcon fontSize="small" /> },
        ]
      },
      {
        title: 'Expense Management',
        items: [
          { label: 'Expense Entry', path: '/expenses/entry', icon: <CurrencyRupeeIcon fontSize="small" /> },
          { label: 'View Expenses', path: '/expenses/view', icon: <VisibilityIcon fontSize="small" /> },
        ]
      }
    ],
    Sales: [
      {
        title: 'Sales Operations',
        items: [
          { label: 'Point of Sale', path: '/sales/pos', icon: <PointOfSaleIcon fontSize="small" /> },
          { label: 'Sales Entry', path: '/sales/pos-keyboard', icon: <ReceiptIcon fontSize="small" /> },
          { label: 'Sales Returns', path: '/sales/returns', icon: <ReturnsIcon fontSize="small" /> },
        ]
      },
      {
        title: 'Sales History',
        items: [
          { label: 'View Sales', path: '/sales/view-orders', icon: <VisibilityIcon fontSize="small" /> },
          { label: 'View Sales Returns', path: '/sales/view-returns', icon: <VisibilityIcon fontSize="small" /> },
        ]
      }
    ],
    Purchases: [
      {
        title: 'Purchase Operations',
        items: [
          { label: 'Purchase Entry', path: '/purchases/po-keyboard', icon: <OrdersIcon fontSize="small" /> },
          { label: 'Purchase Returns', path: '/purchases/returns', icon: <ReturnsIcon fontSize="small" /> },
        ]
      },
      {
        title: 'Purchase History',
        items: [
          { label: 'View Purchases', path: '/purchases/view-orders', icon: <VisibilityIcon fontSize="small" /> },
          { label: 'View Purchase Returns', path: '/purchases/view-returns', icon: <VisibilityIcon fontSize="small" /> },
        ]
      }
    ],
    Reports: [
      {
        title: 'Operational Reports',
        items: [
          { label: 'Products Reports', path: '/reports/products', icon: <ShoppingCartIcon fontSize="small" /> },
          { label: 'Location Sales Reports', path: '/reports/location-sales', icon: <LocationIcon fontSize="small" /> },
          { label: 'Counter Reports', path: '/reports/counter-reports', icon: <PeopleIcon fontSize="small" /> },
          { label: 'Salesman Report', path: '/reports/salesman', icon: <PersonIcon fontSize="small" /> },
          { label: 'Customer Loyalty Report', path: '/reports/customer-loyalty', icon: <CardGiftcardIcon fontSize="small" /> },
          { label: 'Top Selling Products', path: '/reports/top-selling-products', icon: <TrendingUpIcon fontSize="small" /> },
          { label: 'Purchase Reports', path: '/reports/purchases', icon: <LocalShippingIcon fontSize="small" /> },
          { label: 'Stock Report', path: '/reports/stock', icon: <InventoryIcon fontSize="small" /> },
          {
            label: 'Batch Report',
            path: '/reports/batch',
            icon: <LayersIcon fontSize="small" />,
            requiresBatchWise: true,
          },
          { label: 'Stock Transfer Report', path: '/reports/stock-transfer', icon: <ReceiptIcon fontSize="small" /> },
        ]
      },
      {
        title: 'Financial & Tax Reports',
        items: [
          { label: 'Day Close Report', path: '/reports/day-close', icon: <ReceiptIcon fontSize="small" /> },
          { label: 'Day Book', path: '/reports/day-book', icon: <ReceiptIcon fontSize="small" /> },
          { label: 'Billing Reports', path: '/reports/billing', icon: <ReceiptIcon fontSize="small" /> },
          { label: 'Invoice P&L Reports', path: '/reports/invoice-profit-loss', icon: <AssessmentIcon fontSize="small" /> },
          { label: 'Customer Payment', path: '/reports/credit-customers', icon: <PersonIcon fontSize="small" /> },
          { label: 'Supplier Payment', path: '/reports/credit-suppliers', icon: <BusinessIcon fontSize="small" /> },
          { label: 'GSTR1', path: '/reports/gst', icon: <AssessmentIcon fontSize="small" /> },
          { label: 'GSTR2', path: '/reports/gstr2', icon: <AssessmentIcon fontSize="small" /> },
          { label: 'GSTR-3B', path: '/reports/gstr3b', icon: <AssessmentIcon fontSize="small" /> },
          { label: 'HSN Wise Reports', path: '/reports/hsn', icon: <AssessmentIcon fontSize="small" /> },
        ]
      }
    ],
    Settings: [
      {
        title: 'General Settings',
        items: [
          { label: 'Company Settings', path: '/settings/company', icon: <BusinessIcon fontSize="small" /> },
          { label: 'Website Settings', path: '/settings/website', icon: <WebIcon fontSize="small" />, featureKey: 'ecommerce_website' },
          { label: 'Print Settings', path: '/settings/print', icon: <PrintIcon fontSize="small" /> },
          {
            label: 'WhatsApp Configuration',
            path: '/settings/whatsapp',
            icon: <ChatIcon fontSize="small" />,
            featureKey: 'whatsapp_bill_share',
          },
          { label: 'Label Settings', path: '/settings/label-settings', icon: <QrCodeIcon fontSize="small" /> },
          { label: 'Regional Settings', path: '/settings/regional', icon: <CurrencyRupeeIcon fontSize="small" /> },
          { label: 'Loyalty Settings', path: '/settings/loyalty', icon: <CardGiftcardIcon fontSize="small" />, featureKey: 'loyalty_points' },
          { label: 'POS Settings', path: '/settings/page-fields', icon: <PointOfSaleIcon fontSize="small" /> },
          { label: 'Activity Log', path: '/settings/activity-log', icon: <ViewListIcon fontSize="small" /> },
        ]
      },
      {
        title: 'Security & Billing',
        items: [
          { label: 'Role Privileges', path: '/role-privileges', icon: <SecurityIcon fontSize="small" />, adminOnly: true },
          { label: 'Subscription', path: '/subscription', icon: <CreditCardIcon fontSize="small" /> },
        ]
      }
    ]
  };
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [locationManagementExpanded, setLocationManagementExpanded] = useState(false);
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [purchasesExpanded, setPurchasesExpanded] = useState(false);
  const [expensesExpanded, setExpensesExpanded] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [peopleExpanded, setPeopleExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [physicalStockManagementExpanded, setPhysicalStockManagementExpanded] = useState(false);

  // State for notification animations
  const [newNotificationReceived, setNewNotificationReceived] = useState(false);
  const [lastNotificationId, setLastNotificationId] = useState(null);

  // Plan feature flags (loyalty / whatsapp / ecommerce)
  const [planFeatures, setPlanFeatures] = useState(null);

  useEffect(() => {
    const planFeatureDebug = typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('DEBUG_PLAN_FEATURES') === 'true';

    // Load current subscription plan features for feature gating
    getCurrentSubscription()
      .then((sub) => {
        if (planFeatureDebug) {
          console.log('[Plan Features Debug] Raw API response (subscription):', JSON.stringify(sub, null, 2));
          console.log('[Plan Features Debug] sub.plan:', sub?.plan);
          console.log('[Plan Features Debug] sub.plan?.features (raw):', sub?.plan?.features, typeof sub?.plan?.features);
        }

        const rawFeatures = sub && sub.plan && typeof sub.plan.features === 'object'
          ? sub.plan.features
          : {};
        const FLAG_KEYS = ['loyalty_points', 'whatsapp_bill_share', 'ecommerce_website'];

        // Treat as "has feature flags" only if at least one of our known keys is present (even if false)
        const hasAnyFlags = FLAG_KEYS.some((k) => Object.prototype.hasOwnProperty.call(rawFeatures, k));

        // Normalize: API/DB may return boolean true/false or string "true"/"false"
        const isFeatureEnabledValue = (val) => {
          if (val === true) return true;
          if (typeof val === 'string' && val.toLowerCase() === 'true') return true;
          return false;
        };
        const wrapped = {
          loyalty_points: isFeatureEnabledValue(rawFeatures.loyalty_points),
          whatsapp_bill_share: isFeatureEnabledValue(rawFeatures.whatsapp_bill_share),
          ecommerce_website: isFeatureEnabledValue(rawFeatures.ecommerce_website),
          _treatAllEnabled: !hasAnyFlags,
        };

        if (planFeatureDebug) {
          console.log('[Plan Features Debug] rawFeatures:', rawFeatures);
          console.log('[Plan Features Debug] hasAnyFlags:', hasAnyFlags);
          console.log('[Plan Features Debug] wrapped (normalized):', wrapped);
          console.log('[Plan Features Debug] ecommerce_website enabled?', wrapped.ecommerce_website);
          console.log('[Plan Features Debug] Show Website Settings?', wrapped._treatAllEnabled || wrapped.ecommerce_website);
        }

        setPlanFeatures(wrapped);
        // Also expose globally for simple gating inside settings pages
        window.__PLAN_FEATURES__ = wrapped;
        // Broadcast an event so interested pages can reactively update
        try {
          window.dispatchEvent(new CustomEvent('planFeaturesUpdated', { detail: wrapped }));
        } catch (e) {
          // no-op
        }
      })
      .catch((err) => {
        if (planFeatureDebug) {
          console.warn('[Plan Features Debug] getCurrentSubscription failed:', err);
        }
        // On error, keep null so UI behaves as before (all features visible)
        setPlanFeatures(null);
      });
  }, []);

  const isPlanFeatureEnabled = (key) => {
    // While loading or for legacy plans with no flags, treat everything as enabled
    if (!planFeatures || planFeatures._treatAllEnabled) return true;
    const enabled = !!planFeatures[key];
    if (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('DEBUG_PLAN_FEATURES') === 'true' && key === 'ecommerce_website') {
      console.log('[Plan Features Debug] isPlanFeatureEnabled(ecommerce_website):', enabled, 'planFeatures:', planFeatures);
    }
    return enabled;
  };

  // Refs
  const lastFetchTimeRef = useRef(Date.now());

  // Menu states
  const notificationsMenuOpen = Boolean(notificationsAnchorEl);
  const userMenuOpen = Boolean(userMenuAnchorEl);

  // Handle notification animations when unread count changes
  useEffect(() => {
    if (unreadCount > 0) {
      // Trigger animation for new notifications
      setNewNotificationReceived(true);
      setTimeout(() => setNewNotificationReceived(false), 3000);

      // Play sound for new notifications
      playSound();

      // Set the newest unread notification as the last notification
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        setLastNotificationId(unreadNotifications[0].id);
      }
    }
  }, [unreadCount, notifications, playSound]);

  // Menu handlers
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    // Reset animation state
    setNewNotificationReceived(false);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    handleNotificationsClose();

    // Navigate based on notification type, but always to the main page (not specific item)
    if (notification.type === 'SALES_ORDER') {
      navigate('/sales/view-orders');
    } else if (notification.type === 'PURCHASE_ORDER') {
      navigate('/purchases/view-orders');
    } else if (notification.type === 'LOW_STOCK') {
      navigate('/inventory/stock');
    } else {
      navigate('/notifications');
    }

    // Mark notification as read
    if (notification.id && !notification.id.toString().startsWith('low-stock-')) {
      notificationService.markAsRead(notification.id)
        .catch(error => console.error('Error marking notification as read:', error));
    }
  };

  // Mark all notifications as read when viewing them
  const handleMarkAllAsRead = useCallback(() => {
    setNewNotificationReceived(false);

    // Use the context's markAllAsRead function
    notificationService.markAllAsRead()
      .then(() => {
        // Force a fetch after marking all as read to ensure counter is properly updated
        setTimeout(fetchUnreadNotifications, 500);
      })
      .catch(error => console.error('Error marking all notifications as read:', error));
  }, [fetchUnreadNotifications]);

  // Removed automatic mark-as-read on menu open to reduce backend load
  // Now users must manually mark notifications as read
  /*
  useEffect(() => {
    if (notificationsMenuOpen && notifications.some(n => !n.read)) {
      handleMarkAllAsRead();
    }
  }, [notificationsMenuOpen, notifications, handleMarkAllAsRead]);
  */

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    setUserMenuAnchorEl(null);
    logout(navigate);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  // Check if a menu item is active based on the current path
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Toggle submenu expansion
  const handleToggleInventory = () => {
    setInventoryExpanded(!inventoryExpanded);
  };

  const handleToggleLocationManagement = () => {
    setLocationManagementExpanded(!locationManagementExpanded);
  };

  const handleToggleSales = () => {
    setSalesExpanded(!salesExpanded);
  };

  const handleTogglePurchases = () => {
    setPurchasesExpanded(!purchasesExpanded);
  };

  const handleToggleExpenses = () => {
    setExpensesExpanded(!expensesExpanded);
  };

  const handleToggleReports = () => {
    setReportsExpanded(!reportsExpanded);
  };

  const handleTogglePeople = () => {
    setPeopleExpanded(!peopleExpanded);
  };

  const handleToggleSettings = () => {
    setSettingsExpanded(!settingsExpanded);
  };

  const handleTogglePhysicalStockManagement = () => {
    setPhysicalStockManagementExpanded(!physicalStockManagementExpanded);
  };

  // Drawer content with navigation items
  const drawerContent = (
    <>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: [1],
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Typography variant="h6" noWrap component="div">
          Tiya POS
        </Typography>
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ color: 'white' }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List component="nav">
        {/* Dashboard */}
        <ListItem disablePadding>
          <ListItemButton
            selected={isActive('/dashboard')}
            onClick={() => handleNavigate('/dashboard')}
          >
            <ListItemIcon>
              <DashboardIcon color={isActive('/dashboard') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        {/* Inventory */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleInventory}>
            <ListItemIcon>
              <InventoryIcon color={isActive('/inventory') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Inventory" />
            {inventoryExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={inventoryExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/products')}
              onClick={() => handleNavigate('/inventory/products')}
            >
              <ListItemIcon>
                <ViewListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Products" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/import-products')}
              onClick={() => handleNavigate('/inventory/import-products')}
            >
              <ListItemIcon>
                <InventoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Import Products" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/categories')}
              onClick={() => handleNavigate('/inventory/categories')}
            >
              <ListItemIcon>
                <CategoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Categories" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/units')}
              onClick={() => handleNavigate('/inventory/units')}
            >
              <ListItemIcon>
                <StraightenIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Units of Measurement" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/taxes')}
              onClick={() => handleNavigate('/inventory/taxes')}
            >
              <ListItemIcon>
                <CurrencyRupeeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Tax Rates" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/stock-transfer')}
              onClick={() => handleNavigate('/inventory/stock-transfer')}
            >
              <ListItemIcon>
                <InventoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Stock Transfer" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/physical-stock-update')}
              onClick={() => handleNavigate('/inventory/physical-stock-update')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Physical Stock Update" />
            </ListItemButton>
          </List>
        </Collapse>



        {/* Location Management */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleLocationManagement}>
            <ListItemIcon>
              <LocationIcon color={isActive('/inventory/locations') || isActive('/inventory/location-users') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Location Management" />
            {locationManagementExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={locationManagementExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/locations')}
              onClick={() => handleNavigate('/inventory/locations')}
            >
              <ListItemIcon>
                <LocationCityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Locations" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/location-users')}
              onClick={() => handleNavigate('/inventory/location-users')}
            >
              <ListItemIcon>
                <UsersIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Location Users" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Sales */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleSales}>
            <ListItemIcon>
              <ShoppingCartIcon color={isActive('/sales') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Sales" />
            {salesExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={salesExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/sales/pos')}
              onClick={() => handleNavigate('/sales/pos')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Point of Sale" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/sales/pos-keyboard')}
              onClick={() => handleNavigate('/sales/pos-keyboard')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sales" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/sales/view-orders')}
              onClick={() => handleNavigate('/sales/view-orders')}
            >
              <ListItemIcon>
                <ViewListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Orders" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/sales/returns')}
              onClick={() => handleNavigate('/sales/returns')}
            >
              <ListItemIcon>
                <ReturnsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Returns" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/sales/view-returns')}
              onClick={() => handleNavigate('/sales/view-returns')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Returns" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Purchases */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleTogglePurchases}>
            <ListItemIcon>
              <LocalShippingIcon color={isActive('/purchases') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Purchases" />
            {purchasesExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={purchasesExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* Hidden: Old Purchases Orders page (kept in codebase, not shown in sidebar) */}
            {false && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/purchases/orders')}
                onClick={() => handleNavigate('/purchases/orders')}
              >
                <ListItemIcon>
                  <OrdersIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Orders" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/purchases/po-keyboard')}
              onClick={() => handleNavigate('/purchases/po-keyboard')}
            >
              <ListItemIcon>
                <OrdersIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Purchase" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/purchases/view-orders')}
              onClick={() => handleNavigate('/purchases/view-orders')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Orders" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/purchases/returns')}
              onClick={() => handleNavigate('/purchases/returns')}
            >
              <ListItemIcon>
                <ReturnsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Returns" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/purchases/view-returns')}
              onClick={() => handleNavigate('/purchases/view-returns')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Returns" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* People */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleTogglePeople}>
            <ListItemIcon>
              <PeopleIcon color={isActive('/people') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="People" />
            {peopleExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={peopleExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/sales/customers')}
              onClick={() => handleNavigate('/sales/customers')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Customers" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/purchases/suppliers')}
              onClick={() => handleNavigate('/purchases/suppliers')}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Suppliers" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/people/users')}
              onClick={() => handleNavigate('/people/users')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Users" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/people/salesmen')}
              onClick={() => handleNavigate('/people/salesmen')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Salesmen" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Privileges */}
        {isAdmin() && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isActive('/role-privileges')}
                onClick={() => handleNavigate('/role-privileges')}
              >
                <ListItemIcon>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Role Privileges" />
              </ListItemButton>
            </ListItem>
          </>
        )}

        {/* Reports */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleReports}>
            <ListItemIcon>
              <BarChartIcon color={isActive('/reports') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Reports" />
            {reportsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={reportsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>


            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/products')}
              onClick={() => handleNavigate('/reports/products')}
            >
              <ListItemIcon>
                <ShoppingCartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Products Reports" />
            </ListItemButton>



            {/* Hidden: Sales Reports (kept in codebase, not shown in sidebar) */}
            {false && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/reports/sales')}
                onClick={() => handleNavigate('/reports/sales')}
              >
                <ListItemIcon>
                  <ShoppingCartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Sales Reports" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/location-sales')}
              onClick={() => handleNavigate('/reports/location-sales')}
            >
              <ListItemIcon>
                <LocationIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Location Sales Reports" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/counter-reports')}
              onClick={() => handleNavigate('/reports/counter-reports')}
            >
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Counter Reports" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/salesman')}
              onClick={() => handleNavigate('/reports/salesman')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Salesman Report" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/customer-loyalty')}
              onClick={() => handleNavigate('/reports/customer-loyalty')}
            >
              <ListItemIcon>
                <CardGiftcardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Customer Loyalty Report" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/top-selling-products')}
              onClick={() => handleNavigate('/reports/top-selling-products')}
            >
              <ListItemIcon>
                <TrendingUpIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Top Selling Products" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/purchases')}
              onClick={() => handleNavigate('/reports/purchases')}
            >
              <ListItemIcon>
                <LocalShippingIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Purchase Reports" />
            </ListItemButton>
            {/* Hidden: Inventory Reports (kept in codebase, not shown in sidebar) */}
            {false && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/inventory/reports')}
                onClick={() => handleNavigate('/inventory/reports')}
              >
                <ListItemIcon>
                  <InventoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Inventory Reports" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/stock')}
              onClick={() => handleNavigate('/reports/stock')}
            >
              <ListItemIcon>
                <InventoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Stock Report" />
            </ListItemButton>
            {enableBatchWise && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/reports/batch')}
                onClick={() => handleNavigate('/reports/batch')}
              >
                <ListItemIcon>
                  <LayersIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Batch Report" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/day-close')}
              onClick={() => handleNavigate('/reports/day-close')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Day Close Report" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/day-book')}
              onClick={() => handleNavigate('/reports/day-book')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Day Book Report" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/stock-transfer')}
              onClick={() => handleNavigate('/reports/stock-transfer')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Stock Transfer Report" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/billing')}
              onClick={() => handleNavigate('/reports/billing')}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Billing Reports" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/invoice-profit-loss')}
              onClick={() => handleNavigate('/reports/invoice-profit-loss')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Invoice P&L Reports" />
            </ListItemButton>








            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/credit-customers')}
              onClick={() => handleNavigate('/reports/credit-customers')}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Customer Payment" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/credit-suppliers')}
              onClick={() => handleNavigate('/reports/credit-suppliers')}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Supplier Payment" />
            </ListItemButton>

            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/gst')}
              onClick={() => handleNavigate('/reports/gst')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="GST Reports" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/gstr2')}
              onClick={() => handleNavigate('/reports/gstr2')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="GSTR2" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/gstr3b')}
              onClick={() => handleNavigate('/reports/gstr3b')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="GSTR-3B" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/reports/hsn')}
              onClick={() => handleNavigate('/reports/hsn')}
            >
              <ListItemIcon>
                <AssessmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="HSN Wise Reports" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Physical Stock Management */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleTogglePhysicalStockManagement}>
            <ListItemIcon>
              <InventoryIcon color={isActive('/inventory/physical-stock-entry') || isActive('/inventory/view-physical-stock-entry') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Physical Stock Management" />
            {physicalStockManagementExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={physicalStockManagementExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/physical-stock-entry')}
              onClick={() => handleNavigate('/inventory/physical-stock-entry')}
            >
              <ListItemIcon>
                <InventoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Stock Entry" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/inventory/view-physical-stock-entry')}
              onClick={() => handleNavigate('/inventory/view-physical-stock-entry')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Stock Entry" />
            </ListItemButton>
          </List>
        </Collapse>


        {/* Expense Management */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleExpenses}>
            <ListItemIcon>
              <ReceiptIcon color={isActive('/expenses') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Expense Management" />
            {expensesExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={expensesExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/expenses/entry')}
              onClick={() => handleNavigate('/expenses/entry')}
            >
              <ListItemIcon>
                <CurrencyRupeeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Expense Entry" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/expenses/view')}
              onClick={() => handleNavigate('/expenses/view')}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Expenses" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Settings */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleSettings}>
            <ListItemIcon>
              <SettingsIcon color={isActive('/settings') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
            {settingsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/settings/company')}
              onClick={() => handleNavigate('/settings/company')}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Company Settings" />
            </ListItemButton>
            {/* Website Settings – gated by ecommerce_website feature */}
            {isPlanFeatureEnabled('ecommerce_website') && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/settings/website')}
                onClick={() => handleNavigate('/settings/website')}
              >
                <ListItemIcon>
                  <WebIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Website Settings" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/subscription')}
              onClick={() => handleNavigate('/subscription')}
            >
              <ListItemIcon>
                <CreditCardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Subscription & Usage" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/settings/regional')}
              onClick={() => handleNavigate('/settings/regional')}
            >
              <ListItemIcon>
                <CurrencyRupeeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Regional Settings" />
            </ListItemButton>
            {/* Loyalty Settings – gated by loyalty_points feature */}
            {isPlanFeatureEnabled('loyalty_points') && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/settings/loyalty')}
                onClick={() => handleNavigate('/settings/loyalty')}
              >
                <ListItemIcon>
                  <CardGiftcardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Loyalty Settings" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/settings/print')}
              onClick={() => handleNavigate('/settings/print')}
            >
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Print Settings" />
            </ListItemButton>
            {isPlanFeatureEnabled('whatsapp_bill_share') && (
              <ListItemButton
                sx={{ pl: 4 }}
                selected={isActive('/settings/whatsapp')}
                onClick={() => handleNavigate('/settings/whatsapp')}
              >
                <ListItemIcon>
                  <ChatIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="WhatsApp Configuration" />
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/settings/label-settings')}
              onClick={() => handleNavigate('/settings/label-settings')}
            >
              <ListItemIcon>
                <QrCodeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Label Settings" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/settings/page-fields')}
              onClick={() => handleNavigate('/settings/page-fields')}
            >
              <ListItemIcon>
                <PointOfSaleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="POS Settings" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={isActive('/settings/activity-log')}
              onClick={() => handleNavigate('/settings/activity-log')}
            >
              <ListItemIcon>
                <ViewListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Activity Log" />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </>
  );

  return (
    <>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: '100%',
          ml: 0,
          background: 'linear-gradient(to right, #4338ca, #312e81)', // Gradient using new theme primary colors
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexGrow: 0,
              mr: 4,
              cursor: 'pointer'
            }}
            onClick={() => handleNavigate('/dashboard')}
          >
            <Avatar
              src={user.logoUrl ? `${user.logoUrl}${user.logoUrl.includes('?') ? '&' : '?'}_t=${Date.now()}` : ''}
              sx={{
                width: 35,
                height: 35,
                bgcolor: 'white',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              {!user.logoUrl && <BusinessIcon color="primary" sx={{ fontSize: 20 }} />}
            </Avatar>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 'bold' }}
            >
              {user.companyName || 'Tiya Inventory'}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Button
              color="inherit"
              onClick={() => handleNavigate('/dashboard')}
              sx={{
                fontWeight: isActive('/dashboard') ? 'bold' : 'normal',
                textTransform: 'none',
                minWidth: 'auto',
                px: 2,
                borderRadius: '8px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Dashboard
            </Button>

            {Object.keys(megaNavItems).map((menuKey) => (
              <React.Fragment key={menuKey}>
                <Button
                  color="inherit"
                  onClick={(e) => handleNavMenuOpen(e, menuKey)}
                  endIcon={<ExpandMore />}
                  sx={{
                    fontWeight: megaNavItems[menuKey].some(cat => cat.items.some(item => isActive(item.path))) ? 'bold' : 'normal',
                    textTransform: 'capitalize',
                    minWidth: 'auto',
                    px: 2,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  {menuKey}
                </Button>
                <Popover
                  anchorEl={navAnchorEls[menuKey]}
                  open={Boolean(navAnchorEls[menuKey])}
                  onClose={() => handleNavMenuClose(menuKey)}
                  disableRestoreFocus
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      p: 2,
                      width: (menuKey === 'Master' || menuKey === 'Reports') ? 950 : (menuKey === 'Sales' || menuKey === 'Purchases' ? 500 : 450),
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    }
                  }}
                >
                  <Grid container spacing={3}>
                    {megaNavItems[menuKey].map((category) => (
                      <Grid item xs={megaNavItems[menuKey].length === 1 ? 12 : (megaNavItems[menuKey].length === 2 ? 6 : (megaNavItems[menuKey].length === 3 ? 4 : 3))} key={category.title}>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{
                            fontWeight: 'bold',
                            mb: 1.5,
                            pb: 0.5,
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            display: 'block'
                          }}
                        >
                          {category.title}
                        </Typography>
                        <List dense disablePadding>
                          {category.items
                            .filter((item) => {
                              if (item.featureKey) {
                                return isPlanFeatureEnabled(item.featureKey);
                              }
                              if (item.requiresBatchWise && !enableBatchWise) {
                                return false;
                              }
                              return true;
                            })
                            .map((item) => (
                              (!item.adminOnly || isAdmin()) && (
                                <ListItemButton
                                  key={item.path}
                                  onClick={() => {
                                    handleNavigate(item.path);
                                    handleNavMenuClose(menuKey);
                                  }}
                                  selected={isActive(item.path)}
                                  sx={{
                                    borderRadius: '6px',
                                    mb: 0.5,
                                    '&.Mui-selected': {
                                      bgcolor: '#e3f2fd', // Light professional blue
                                      color: 'primary.main',
                                      '& .MuiListItemIcon-root': { color: 'primary.main' }
                                    },
                                    '&.Mui-selected:hover': {
                                      bgcolor: '#bbdefb', // Slightly darker on hover
                                    }
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32, minHeight: 0 }}>
                                    {item.icon}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                  />
                                </ListItemButton>
                              )
                            ))}
                        </List>
                      </Grid>
                    ))}
                  </Grid>
                </Popover>
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* POS Button */}
            <Tooltip title="Sales Keyboard">
              <Box
                component="button"
                onClick={() => handleNavigate('/sales/pos-keyboard')}
                sx={{
                  mr: 1,
                  bgcolor: '#00c4b4',
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: '#00a89a'
                  },
                  boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                }}
              >
                <AppsIcon sx={{ color: 'white', fontSize: 20, mr: 0.75 }} />
                <Typography
                  variant="button"
                  component="span"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                  }}
                >
                  POS
                </Typography>
              </Box>
            </Tooltip>

            {/* Offline bills pending sync (desktop app only) */}
            <OfflineSyncIndicator />

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                onClick={handleNotificationsOpen}
                size="large"
                sx={{
                  animation: newNotificationReceived ?
                    `${pulseAnimation} 0.8s ease-in-out infinite` : 'none',
                  position: 'relative',
                  mr: 1
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  showZero={false}
                  sx={{
                    '& .MuiBadge-badge': {
                      animation: newNotificationReceived ?
                        `${notificationDotAnimation} 0.5s ease-in-out` : 'none',
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px'
                    }
                  }}
                >
                  <NotificationsIcon sx={{
                    fontSize: newNotificationReceived ? 28 : 24,
                    transition: 'all 0.3s ease'
                  }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Profile Menu */}
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleUserMenuOpen}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={userMenuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={userMenuOpen ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user.name?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationsAnchorEl}
            open={notificationsMenuOpen}
            onClose={handleNotificationsClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                width: 350,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Notifications</Typography>
              {unreadCount > 0 && (
                <Box
                  component="button"
                  onClick={handleMarkAllAsRead}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Mark all as read
                </Box>
              )}
            </Box>

            <Divider />

            {notifications.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ width: '100%' }}>
                  No notifications
                </Typography>
              </MenuItem>
            ) : (
              notifications.slice(0, 5).map((notification, index) => (
                <React.Fragment key={notification.id || index}>
                  <MenuItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          display: 'block',
                          textAlign: 'right',
                          fontSize: '0.7rem'
                        }}
                      >
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </MenuItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
            {notifications.length > 0 && (
              <>
                <Divider />
                <MenuItem onClick={() => {
                  handleNotificationsClose();
                  handleNavigate('/notifications');
                }}>
                  <Typography variant="body2" color="primary" align="center" sx={{ width: '100%' }}>
                    View all notifications
                  </Typography>
                </MenuItem>
              </>
            )}
          </Menu>

          {/* User Menu */}
          <Menu
            anchorEl={userMenuAnchorEl}
            id="account-menu"
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleUserMenuClose} disabled sx={{ pointerEvents: 'none' }}>
              <Typography variant="subtitle2">{user.name || 'User'}</Typography>
            </MenuItem>
            <MenuItem onClick={handleUserMenuClose} disabled sx={{ pointerEvents: 'none' }}>
              <Typography variant="body2" color="text.secondary">{user.email || ''}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              handleUserMenuClose();
              handleNavigate('/profile');
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar >

      {/* Side Navigation Drawer (Mobile Only) */}
      < Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', sm: 'none' }, // Only show on extra small screens
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            zIndex: theme.zIndex.drawer,
            height: '100%',
            borderRight: 'none',
          },
        }
        }
      >
        {drawerContent}
      </Drawer >
    </>
  );
};

export default Navigation; 
